import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { PageContainer } from '@/components/layout/PageContainer';
import { AdminPageHeader } from '@/components/admin/shared/AdminPageHeader';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { PageTabs, type PageTabDefinition } from '@/components/common/PageTabs';
import { MembersManagementSection } from '@/components/practitioner/TeamPage/MembersManagementSection';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationQuery } from '@/hooks/useOrganization';
import {
  ACTIVITY_LOG_CLIENT_QUERY_PARAMETER,
  ACTIVITY_LOG_TAB_QUERY_PARAMETER,
  ACTIVITY_LOG_TAB_QUERY_VALUE,
} from '@/constants/activityLog';
import { EMAIL_LOG_TAB_QUERY_VALUE } from '@/constants/emailLog';
import { WORKSPACE_DATA_TAB_QUERY_VALUE } from '@/constants/workspaceData';
import { DataAndClosureSection } from './DataAndClosure';
import { EmailLogSection } from './EmailLog';
import { OrganizationForm } from './OrganizationForm';
import { WorkspaceActivitySection } from './WorkspaceActivitySection';

type OrganizationTabId =
  | 'details'
  | 'members'
  | typeof ACTIVITY_LOG_TAB_QUERY_VALUE
  | typeof EMAIL_LOG_TAB_QUERY_VALUE
  | typeof WORKSPACE_DATA_TAB_QUERY_VALUE;

const ORGANIZATION_TAB_IDS: OrganizationTabId[] = [
  'details',
  'members',
  ACTIVITY_LOG_TAB_QUERY_VALUE,
  EMAIL_LOG_TAB_QUERY_VALUE,
  WORKSPACE_DATA_TAB_QUERY_VALUE,
];

function resolveOrganizationTabId(candidate: string | null): OrganizationTabId {
  const matchingTabId = ORGANIZATION_TAB_IDS.find(function matches(tabId) {
    return tabId === candidate;
  });
  return matchingTabId === undefined ? 'details' : matchingTabId;
}

export function OrganizationPage() {
  const { t } = useTranslation(['settings', 'team', 'activity', 'emailLog']);
  const { activeWorkspace, isElevatedWorkspaceAccess } = useAuth();
  const isOwner =
    (activeWorkspace !== null && activeWorkspace.isOwner === true) || isElevatedWorkspaceAccess;
  const [searchParameters, setSearchParameters] = useSearchParams();
  const activeTabId = resolveOrganizationTabId(
    searchParameters.get(ACTIVITY_LOG_TAB_QUERY_PARAMETER)
  );
  const activityClientId = searchParameters.get(ACTIVITY_LOG_CLIENT_QUERY_PARAMETER);

  function selectTab(tabId: string) {
    setSearchParameters({ [ACTIVITY_LOG_TAB_QUERY_PARAMETER]: tabId });
  }

  function clearActivityClient() {
    setSearchParameters({ [ACTIVITY_LOG_TAB_QUERY_PARAMETER]: ACTIVITY_LOG_TAB_QUERY_VALUE });
  }

  if (isOwner === false) {
    return <Navigate to={APPLICATION_ROUTES.workspaceDashboard} replace />;
  }

  const tabs: PageTabDefinition[] = [
    { id: 'details', label: t('settings:organization.tabs.details') },
    { id: 'members', label: t('settings:organization.tabs.members') },
    { id: ACTIVITY_LOG_TAB_QUERY_VALUE, label: t('activity:title') },
    { id: EMAIL_LOG_TAB_QUERY_VALUE, label: t('emailLog:title') },
    { id: WORKSPACE_DATA_TAB_QUERY_VALUE, label: t('settings:organization.tabs.data') },
  ];

  return (
    <PageContainer width="full">
      <AdminPageHeader
        title={t('settings:organization.title')}
        subtitle={t('settings:organization.subtitle')}
      />

      <PageTabs
        tabs={tabs}
        activeTabId={activeTabId}
        onSelect={selectTab}
        ariaLabel={t('settings:organization.title')}
        idPrefix="organization"
      >
        {activeTabId === 'details' ? <OrganizationDetailsPanel /> : null}
        {activeTabId === 'members' ? <MembersManagementSection /> : null}
        {activeTabId === ACTIVITY_LOG_TAB_QUERY_VALUE ? (
          <WorkspaceActivitySection
            clientId={activityClientId}
            onClearClient={clearActivityClient}
          />
        ) : null}
        {activeTabId === EMAIL_LOG_TAB_QUERY_VALUE ? <EmailLogSection /> : null}
        {activeTabId === WORKSPACE_DATA_TAB_QUERY_VALUE ? <DataAndClosureSection /> : null}
      </PageTabs>
    </PageContainer>
  );
}

function OrganizationDetailsPanel() {
  const organizationQuery = useOrganizationQuery(true);

  if (organizationQuery.isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  if (organizationQuery.isError === true || organizationQuery.data === undefined) {
    return (
      <QueryErrorState
        onRetry={function retry() {
          organizationQuery.refetch();
        }}
      />
    );
  }

  return <OrganizationForm organization={organizationQuery.data} />;
}
